import type {
  StructuralPassFailGroupSeries,
  StructuralPassFailSummary,
} from '@models/interfaces';
import { ReportsGroupBy } from '@models/interfaces';
import type {
  StructuralPassFailGroupBy,
  StructuralStatus,
} from './structural-pass-fail.constants';
import {
  STRUCTURAL_ANALYSIS_STATUS_ORDER,
  STRUCTURAL_PASS_FAIL_GROUP_BY_VALUES,
  STRUCTURAL_PASS_FAIL_GROUP_TITLE_BY_GROUP_BY,
  STRUCTURAL_STATUS_LABELS,
} from './structural-pass-fail.constants';

export interface StructuralAnalysisResultsChartItem {
  count: number;
  displayLabel: string;
  status: StructuralStatus;
}

export interface StructuralGroupSeriesChartItem
  extends StructuralPassFailGroupSeries {
  groupLabel: string;
}

export interface StructuralPassRateGaugeItem {
  fill: string;
  name: string;
  value: number;
}

export const getStructuralAnalysisResultsChartData = (
  summary?: StructuralPassFailSummary,
): StructuralAnalysisResultsChartItem[] =>
  STRUCTURAL_ANALYSIS_STATUS_ORDER.map(
    (status: StructuralStatus): StructuralAnalysisResultsChartItem => ({
      count: summary?.[status] ?? 0,
      displayLabel: STRUCTURAL_STATUS_LABELS[status],
      status,
    }),
  );

export const getStructuralGroupSeriesChartData = (
  groupSeries: StructuralPassFailGroupSeries[] = [],
): StructuralGroupSeriesChartItem[] =>
  groupSeries.map(
    (group: StructuralPassFailGroupSeries): StructuralGroupSeriesChartItem => ({
      ...group,
      groupLabel: group.groupName || 'Unassigned',
    }),
  );

export const getStructuralPassRate = (
  summary?: StructuralPassFailSummary,
): number => Math.max(0, Math.min(100, summary?.passRate ?? 0));

export const formatStructuralPassRate = (value?: number): string =>
  `${(value ?? 0).toFixed(2)}%`;

export const getStructuralPassRateGaugeData = (
  passRate: number,
): StructuralPassRateGaugeItem[] => [
  {
    fill: '#03ffd2',
    name: 'Pass Rate',
    value: passRate,
  },
  {
    fill: 'rgba(255,255,255,0.12)',
    name: 'Remaining',
    value: 100 - passRate,
  },
];

export const getStructuralPassFailGroupTitle = (
  groupBy?: ReportsGroupBy,
): string => {
  const selectedGroupBy = STRUCTURAL_PASS_FAIL_GROUP_BY_VALUES.includes(
    groupBy as StructuralPassFailGroupBy,
  )
    ? (groupBy as StructuralPassFailGroupBy)
    : ReportsGroupBy.ProjectType;

  return `Pass vs Fail by ${STRUCTURAL_PASS_FAIL_GROUP_TITLE_BY_GROUP_BY[selectedGroupBy]}`;
};
